import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AuthProvider } from "@/stores/auth";
import { ToastProvider } from "@/components/Toast";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/Login";

// Lazy-load heavy pages so that the login screen renders immediately
// and plotly.js (≈5 MB) is only fetched when actually needed.
const WorkspacesPage = lazy(() => import("@/pages/Workspaces"));
const DashboardPage = lazy(() => import("@/pages/Dashboard"));
const PipelineEditorPage = lazy(() => import("@/pages/PipelineEditor"));
const ReportEditorPage = lazy(() => import("@/pages/ReportEditor"));
const ReportViewerPage = lazy(() => import("@/pages/ReportViewer"));
const SecurityPage = lazy(() => import("@/pages/Security"));
const ModelsPage = lazy(() => import("@/pages/Models"));
const ProfilePage = lazy(() => import("@/pages/Profile"));
const NotFoundPage = lazy(() => import("@/pages/NotFound"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPassword"));
const OnboardingPage = lazy(() => import("@/pages/Onboarding"));
const SubscriptionPage = lazy(() => import("@/pages/Subscription"));
const DashboardCompositePage = lazy(() => import("@/pages/DashboardComposite"));
const AdminPage = lazy(() => import("@/pages/Admin"));
const FAQPage = lazy(() => import("@/pages/FAQ"));
const GuidePage = lazy(() => import("@/pages/Guide"));
const LandingPage = lazy(() => import("@/pages/Landing"));
const BetaSignupPage = lazy(() => import("@/pages/BetaSignup"));

function PageLoader() {
  return <div className="loading">Chargement…</div>;
}

const router = createBrowserRouter([
  {
    path: "/landing",
    element: <LandingPage />,
  },
  {
    path: "/beta-signup",
    element: <BetaSignupPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: "workspaces",
        element: <WorkspacesPage />,
      },
      {
        path: "workspaces/:workspaceId",
        element: <WorkspacesPage />,
      },
      {
        path: "pipelines",
        element: <PipelineEditorPage />,
      },
      {
        path: "reports",
        element: <ErrorBoundary><ReportEditorPage /></ErrorBoundary>,
      },
      {
        path: "view",
        element: <ErrorBoundary><ReportViewerPage /></ErrorBoundary>,
      },
      {
        path: "security",
        element: <SecurityPage />,
      },
      {
        path: "admin",
        element: <AdminPage />,
      },
      {
        path: "models",
        element: <ModelsPage />,
      },
      {
        path: "profile",
        element: <ProfilePage />,
      },
      {
        path: "onboarding",
        element: <OnboardingPage />,
      },
      {
        path: "subscription",
        element: <SubscriptionPage />,
      },
      {
        path: "dashboards",
        element: <DashboardCompositePage />,
      },
      {
        path: "faq",
        element: <FAQPage />,
      },
      {
        path: "guide",
        element: <GuidePage />,
      },
    ],
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <Suspense fallback={<PageLoader />}>
            <RouterProvider router={router} />
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
